import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CCard, CCardBody } from '@coreui/react';
import PatientAppointmentDetails from '../../services/general/appointmentData.api';

const SubmitFinalInfo = ({ diagnosis = [], formData = {} }) => {
    // Get today's date
    const currentDate = new Date().toLocaleDateString('en-US');
    const fallbackProviderName = formData?.ProviderName || diagnosis?.[0]?.ProviderName || '';
    const [providerName, setProviderName] = useState(fallbackProviderName);

    const getAppointmentData = async () => {
        try {
            const result = await PatientAppointmentDetails('GET', '', '', '', '');
            const response = typeof result?.d === 'string' ? JSON.parse(result.d) : result?.d || result;
            const rawData = response?.Data || [];
            const appointments = [];

            rawData.forEach((item) => {
                const rawDetails = item?.AppointmentDetails ?? item?.appointmentDetails;
                let parsedDetails = rawDetails;

                if (typeof rawDetails === 'string') {
                    try {
                        parsedDetails = JSON.parse(rawDetails);
                    } catch (error) {
                        console.warn(
                            `Skipping malformed appointment details for schedule ${item?.ID || item?.Id || 'unknown'}:`,
                            error,
                        );
                        return;
                    }
                }

                if (!Array.isArray(parsedDetails)) {
                    console.warn('Unexpected appointment details format:', item?.ID || item?.Id || 'unknown');
                    return;
                }

                parsedDetails.forEach((parsedItem) => {
                    const patientID = parsedItem?.patientID;
                    const appointmentDetails = parsedItem?.appointmentDetails || [];

                    appointmentDetails.forEach((appointment) => {
                        const data = appointment?.data || {};
                        const current = appointment?.current || {};

                        appointments.push({
                            patientID: patientID || data?.PatientID || current?.Patient?.value,
                            data,
                            current: {
                                ...current,
                                Patient: current?.Patient || {
                                    value: data?.PatientID || patientID || '',
                                    label: data?.AppointmentName || 'Unknown',
                                },
                                Provider: current?.Provider || {
                                    value: data?.ProviderID || '',
                                    label: data?.ProviderName || data?.Provider || '',
                                },
                            },
                        });
                    });
                });
            });

            return appointments.filter((appointment) => {
                const status = String(appointment.data?.Status || '').trim().toLowerCase();
                return status === 'scheduled' || status === 'rescheduled';
            });
        } catch (error) {
            console.error('Error in getAppointmentData:', error);
            return [];
        }
    };

    useEffect(() => {
        let isCurrent = true;
        setProviderName(fallbackProviderName);

        const fetchData = async () => {
            const appointments = await getAppointmentData();
            if (!isCurrent) return;

            const patientId = String(formData?.PatientID || '').trim();
            const patientAppointments = appointments.filter((appointment) => {
                const appointmentPatientId =
                    appointment.current?.Patient?.value ||
                    appointment.data?.PatientID ||
                    appointment.patientID;
                return String(appointmentPatientId || '').trim() === patientId;
            });
            const matchedProviderName = patientAppointments
                .map((appointment) => [
                    appointment.current?.Provider?.label,
                    appointment.current?.Provider?.Name,
                    appointment.data?.ProviderName,
                    appointment.data?.Provider?.label,
                    typeof appointment.data?.Provider === 'string' ? appointment.data.Provider : '',
                ]
                    .map((name) => String(name || '').trim())
                    .find((name) => name && !['unknown', 'n/a', 'none'].includes(name.toLowerCase())))
                .find(Boolean);

            if (matchedProviderName) {
                setProviderName(matchedProviderName);
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
        };
    }, [formData?.PatientID, fallbackProviderName]);


    return (
        <div className="note-details-content">
            {diagnosis.map((patient) => (
                <CCard
                    key={patient.Id}
                    className="mb-4 note-details-info-card"
                    style={{
                        marginTop: '24px',
                        borderRadius: '12px',
                        padding: '20px',
                        border: '1px solid rgb(113, 113, 113)',
                        background: 'var(--ui-bg-modal-body, var(--ui-dashboard-surface, #ffffff))',
                        color: 'var(--ui-text-primary, #000000)',
                    }}
                >
                    <CCardBody>
                        <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>

                            <li
                                className="note-details-info-row"
                                style={{
                                    marginBottom: '15px',
                                    paddingBottom: '10px',
                                    borderBottom: '1px solid rgb(113, 113, 113)',
                                }}
                            >
                                <span>Facility Name:</span>
                                <span style={{ marginLeft: '10px', color: 'var(--ui-text-secondary, #555555)' }}>
                                    {patient.FacilityName || 'N/A'}
                                </span>
                            </li>


                            <li
                                className="note-details-info-row"
                                style={{
                                    marginBottom: '15px',
                                    paddingBottom: '10px',
                                    borderBottom: '1px solid rgb(113, 113, 113)',
                                }}
                            >
                                <span>Room:</span>
                                <span style={{ marginLeft: '10px', color: 'var(--ui-text-secondary, #555555)' }}>
                                    {patient.Room || 'N/A'}
                                </span>
                            </li>


                            <li
                                className="note-details-info-row"
                                style={{
                                    marginBottom: '15px',
                                    paddingBottom: '10px',
                                    borderBottom: '1px solid rgb(113, 113, 113)',
                                }}
                            >
                                <span>Diagnosis:</span>
                                <span style={{ marginLeft: '10px', color: 'var(--ui-text-secondary, #555555)' }}>
                                    {(patient.Diagnosis || '')
                                        .split(',')
                                        .map((item) => item.trim())
                                        .filter(Boolean)
                                        .join(', ') || 'N/A'}
                                </span>
                            </li>


                            <li
                                className="note-details-info-row"
                                style={{
                                    marginBottom: '15px',
                                    paddingBottom: '10px',
                                    borderBottom: '1px solid rgb(113, 113, 113)',
                                }}
                            >
                                <span>Provider Name:</span>
                                <span style={{ marginLeft: '10px', color: 'var(--ui-text-secondary, #555555)' }}>
                                    {providerName || 'N/A'}
                                </span>

                            </li>


                            <li
                                className="note-details-info-row"
                                style={{
                                    marginBottom: '15px',
                                    paddingBottom: '10px',
                                    borderBottom: '1px solid rgb(113, 113, 113)',
                                }}
                            >
                                <span>Created Date:</span>
                                <span style={{ marginLeft: '10px', color: 'var(--ui-text-secondary, #555555)' }}>
                                    {new Date(currentDate).toLocaleDateString('en-US')}
                                </span>
                            </li>
                            <li
                                className="note-details-info-row"
                                style={{
                                    marginBottom: '15px',
                                    paddingBottom: '10px',
                                    borderBottom: '1px solid rgb(113, 113, 113)',
                                }}
                            >
                                <span>Last Visit Date:</span>
                                <span style={{ marginLeft: '10px', color: 'var(--ui-text-secondary, #555555)' }}>
                                    {currentDate}
                                </span>
                            </li>
                            <li>
                                <span>Last Updated Date:</span>
                                <span style={{ marginLeft: '10px', color: 'var(--ui-text-secondary, #555555)' }}>
                                    {currentDate}
                                </span>
                            </li>
                        </ul>
                    </CCardBody>
                </CCard>
            ))}
        </div>


    );
};

SubmitFinalInfo.propTypes = {
    diagnosis: PropTypes.arrayOf(PropTypes.shape({
        Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        ProviderName: PropTypes.string,
    })),
    formData: PropTypes.shape({
        PatientID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        ProviderName: PropTypes.string,
    }),
};

export default SubmitFinalInfo;
